import { ArrowTopRightOnSquareIcon, CogIcon } from '@heroicons/react/24/outline';
import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useState } from 'react';

import CLocalizedTextStringStatic from '../../../components/CLocalizedTextStringStatic';
import CPageModule from '../../../components/CPageModule';
import TagButton from '../../../components/TagButton';
import ActionLinkButton from '../../../components/dashboardContent/ActionLinkButton';
import Page from '../../../components/layout/Page';
import PageModuleTitle from '../../../components/layout/PageModuleTitle';
import XDResponsiveGridWrapper from '../../../components/layout/XDResponsiveGridWrapper';
import { ETonalButtonClassNames } from '../../../enums/ETagButtonColors';
import { EUserRoles } from '../../../enums/EUserRoles';
import { PageRoutes } from '../../../enums/enums';
import { updateState } from '../../../helpers/helper';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import UserManagementService from '../../../services/userManagementService';
import { updateSnackbar } from '../../../store/appSlice';
import { userHasRole } from '../../../utils/utils';

interface State {
  adminUrl?: string;
}

export default function UserManagementIndex() {
  const { firstName, lastName, email, id, isAdmin, isStaff, isPortfolioManager, isCurrentUser, roles } = useAppSelector(
    (state) => state.user
  );
  const dispatch = useAppDispatch();
  const keycloak = useKeycloak();
  const isProfessionalClient = roles?.includes(EUserRoles.PROFESSIONAL_CLIENT);
  const isSuitableCounterparty = roles?.includes(EUserRoles.SUITABLE_COUNTERPARTY);
  const isViewerAdmin = userHasRole(keycloak.keycloak.tokenParsed, [[EUserRoles.ADMIN]]);
  const [state, setState] = useState<State>({});
  const accountManagementText = CLocalizedTextStringStatic('globalManageAccount');
  const viewInKeycloakText = CLocalizedTextStringStatic('userManagement.userHub.viewInKeycloak');

  useEffect(() => {
    const retrieveAdminUrl = async () => {
      const res = await UserManagementService.getKeycloakAdminUrl();
      if (!res.hasError()) {
        updateState<State>({ adminUrl: res.getData().url }, state, setState);
      } else {
        dispatch(updateSnackbar({ message: res.getErrorString(), severity: 'error' }));
      }
    };

    // Keycloak admin URL is admin-only, staff have read-only access
    if (!isViewerAdmin) {
      return;
    }

    (async () => {
      await retrieveAdminUrl();
    })();
  }, []);

  return (
    <Page>
      <CPageModule>
        <PageModuleTitle subtitle={email} standardCase flipOrder>
          <div className={'flex flex-wrap gap-2 items-center'}>
            <h1>{[firstName, lastName].join(' ')}</h1>
            {isAdmin && <TagButton color={ETonalButtonClassNames.TEAL_PPT_4}>Admin</TagButton>}
            {isStaff && <TagButton color={ETonalButtonClassNames.TEAL_GREEN_1}>Staff</TagButton>}
            {isPortfolioManager && <TagButton color={ETonalButtonClassNames.TEAL_PPT_1}>Portfolio Manager</TagButton>}
            {isCurrentUser && <TagButton color={ETonalButtonClassNames.TEAL_PPT_6}>You</TagButton>}
            {isProfessionalClient && (
              <TagButton color={ETonalButtonClassNames.TEAL_PPT_2}>Professional Client</TagButton>
            )}
            {isSuitableCounterparty && (
              <TagButton color={ETonalButtonClassNames.TEAL_PPT_3}>Eligible Counterparty</TagButton>
            )}
          </div>
        </PageModuleTitle>
        <XDResponsiveGridWrapper>
          {/* <ActionLinkButton label="Backtest access" to={PageRoutes.USERS_BACKTEST} disabled>
            <DocumentChartBarIcon />
          </ActionLinkButton> */}
          {isViewerAdmin && (
            <ActionLinkButton label={accountManagementText} to={PageRoutes.USERS_USER_HUB_MANAGE_ACCOUNT}>
              <CogIcon />
            </ActionLinkButton>
          )}
          {state.adminUrl && (
            <ActionLinkButton
              label={viewInKeycloakText}
              target={'_blank'}
              to={`${state.adminUrl}/admin/${keycloak.keycloak.realm}/console/#/${keycloak.keycloak.realm}/users/${id}/settings`}
            >
              <ArrowTopRightOnSquareIcon />
            </ActionLinkButton>
          )}
        </XDResponsiveGridWrapper>
      </CPageModule>
    </Page>
  );
}
